/**
 * v8 Letter Throwing Animation Types
 *
 * Type definitions for the Letter Throwing animation system.
 * Used by both web (CSS) and native (Reanimated) implementations.
 */

export type SpeedPreset = '1' | '1.25' | '1.5' | '1.8' | '2' | '2.5' | '3';

/**
 * Animation variant for A/B testing different animation styles.
 * - 'typewriter': Simple text reveal with cursor (current default)
 * - 'letterThrowing': Full letter throwing with chromatic layers (matches native)
 * - 'wordConvergence': Letters converge to center forming words progressively
 * - 'fullExperience': Complete 5-phase animation with reading stage and result badge (matches HTML reference)
 * - 'letterZoom': Camera zoom-out paradigm - single scaled element zooms from letter to line (O(1) performance)
 */
export type AnimationVariant = 'typewriter' | 'letterThrowing' | 'wordConvergence' | 'fullExperience' | 'letterZoom';

/**
 * Word Convergence sub-phases within emergence
 * Represents "thoughts crystallizing into text" visual metaphor
 */
export type ConvergencePhase =
  | 'chaos'         // 0-20%: Scattered letters with high chromatic aberration
  | 'converging'    // 20-70%: Letters flying to word positions in center
  | 'lineForming'   // 70-90%: Words accumulating into first line
  | 'transitioning'; // 90-100%: Smooth handoff to typewriter reading

/**
 * Pre-calculated letter position for word convergence animation
 */
export interface LetterPosition {
  /** Global index in full text */
  index: number;
  /** Character at this position */
  char: string;
  /** Word index this letter belongs to */
  wordIndex: number;
  /** Letter index within its word */
  letterInWord: number;
  /** Target X position (relative to center) */
  targetX: number;
  /** Target Y position (relative to center) */
  targetY: number;
  /** Whether this letter has landed */
  landed: boolean;
}

/**
 * Word convergence animation state
 */
export interface WordConvergenceState {
  /** Current sub-phase within convergence */
  phase: ConvergencePhase;
  /** Progress within current phase (0-1) */
  phaseProgress: number;
  /** All pre-calculated letter positions */
  letterPositions: LetterPosition[];
  /** Current word being formed */
  currentWordIndex: number;
  /** Current letter within word being animated */
  currentLetterInWord: number;
  /** Formed text so far */
  formedText: string;
  /** Width of formed text (for line detection) */
  formedWidth: number;
  /** Is first line complete? */
  lineComplete: boolean;
}

export interface SpeedSettings {
  /** Speed multiplier (1.0 - 3.0) */
  multiplier: number;
  /** Human-readable label */
  label: string;
  /** Landing animation intensity scale */
  bounceScale: number;
  /** Chromatic split offset in pixels */
  chromaOffset: number;
  /** Maximum blur effect intensity */
  blurMax: number;
  /** Minimum letter delay in ms */
  minLetter: number;
  /** Minimum word pause in ms */
  minWord: number;
  /** Minimum flight duration in ms */
  minFlight: number;
}

export type AnimationPhase =
  | 'idle'
  | 'emergence' // Letter throwing (55% of duration)
  | 'transition' // Fade to reading (5%)
  | 'reading' // Saccadic scroll (25%)
  | 'convergence' // Shrink (8%)
  | 'revelation' // Pre-complete reveal phase
  | 'complete'; // Show results (7%)

export interface AnimationState {
  phase: AnimationPhase;
  progress: number; // 0-1
  currentLetterIndex: number;
  currentWordIndex: number;
  isSkipping: boolean;
  emergenceComplete: boolean;
}

/** Analysis result for drama badge reveal (revelation phase) */
export interface AnalysisResultForAnimation {
  /** Drama score (0-10) */
  score: number;
  /** Category key for styling (safe, low, caution, moderate, concern, high, high_drama) */
  category: string;
  /** Optional summary text */
  summary?: string;
}

export interface LetterThrowingAnimationProps {
  /** Drama text to animate */
  text: string;
  /** Speed preset (default: '3' for Express) */
  speed?: SpeedPreset;
  /** Callback when animation completes */
  onComplete: () => void;
  /** Callback when user taps to skip */
  onSkip?: () => void;
  /** Start immediately on mount (default: true) */
  autoStart?: boolean;
  /** Show letter progress indicator (default: false) */
  showProgress?: boolean;
  /** Allow speed selection during animation (default: false) */
  showSpeedSelector?: boolean;
  /** Analysis result for drama badge reveal (optional - shown in revelation phase) */
  analysisResult?: AnalysisResultForAnimation;
  /** Container style */
  style?: import('react-native').StyleProp<import('react-native').ViewStyle>;
  /** Animation variant for A/B testing (default: 'typewriter' on web) */
  variant?: AnimationVariant;
  /** Theatrical mode configuration (optional - uses defaults if not provided) */
  theatricalConfig?: import('@/lib/theatrical/theatricalModeConfig').TheatricalModeConfig;
  /**
   * Streamed narrator text from LLM response (replaces legacy quips when provided).
   * Shows in narrator subtitle area during emergence phase.
   */
  narratorStreamedText?: string;
  /**
   * User's subscription tier for tier-aware quip selection.
   * Falls back to 'anonymous' if not provided.
   */
  userTier?: 'anonymous' | 'free' | 'drama_plus' | 'dramax';
}

export interface FlyingLetterProps {
  /** Character to display */
  char: string;
  /** Index in the full text */
  index: number;
  /** Speed settings from current preset */
  speedSettings: SpeedSettings;
  /** Callback when letter lands */
  onLand: () => void;
  /** Target position for landing */
  targetPosition?: { x: number; y: number };
}

export interface AccumulatedTextProps {
  /** Characters that have landed */
  landedText: string;
  /** Current word index for styling */
  currentWordIndex: number;
  /** Speed settings for bounce animation */
  speedSettings: SpeedSettings;
}

/** Chromatic layer colors (Y2K palette) */
export const CHROMATIC_COLORS = {
  r: '#FFB3E6', // Pink
  g: '#D4A5FF', // Lavender
  b: '#A5FFEB', // Mint
  main: '#FFFFFF', // White
} as const;

/**
 * Phase timing percentages (default for fullExperience variant)
 *
 * Balanced for 8s total animation @1.25x speed:
 * - Emergence: 25% (2s dramatic reveal, 3 lines)
 * - Reading: 50% (4s relaxed scrolling, 6 lines)
 * - Convergence: 12% (~1s shrink)
 * - Time per line: ~667ms (balanced across phases)
 *
 * @see ANIMATION_DURATION_CONSTANTS - Base duration and floor
 * @see ANIMATION_LINE_CONSTANTS - Line limits for pacing balance
 */
export const PHASE_TIMING = {
  emergence: 0.25, // 0% - 25% (2s @8s total - dramatic reveal)
  transition: 0.05, // 25% - 30%
  reading: 0.50, // 30% - 80% (4s @8s total - relaxed scrolling)
  convergence: 0.12, // 80% - 92% (~1s @8s total)
  revelation: 0.08, // 92% - 100% (0.64s @8s total)
} as const;

/**
 * Phase timing for letterZoom variant
 *
 * Balanced for 8s total @1.25x speed:
 * - Emergence: 2s dramatic reveal (3 lines)
 * - Reading: 4s relaxed scrolling (6 lines)
 * - Convergence: ~1s shrink animation
 * - Time per line: ~667ms (balanced across phases)
 */
export const LETTER_ZOOM_PHASE_TIMING = {
  emergence: 0.25, // 0% - 25% (2s @8s - dramatic zoom sequence)
  transition: 0.05, // 25% - 30% (bridge crossfade)
  reading: 0.50, // 30% - 80% (4s @8s - relaxed scrolling)
  convergence: 0.12, // 80% - 92% (~1s @8s - shrink)
  revelation: 0.08, // 92% - 100% (0.64s @8s - badge reveal)
} as const;

/**
 * Pre-calculated phase boundaries for letterZoom variant
 * Explicit boundaries avoid cumulative calculation errors in components
 * Used by LetterZoomAnimation.native.tsx and LetterZoomAnimation.web.tsx
 *
 * Boundaries for balanced 8s animation @1.25x speed
 */
export const LETTER_ZOOM_PHASE_BOUNDARIES = {
  emergenceEnd: 0.25, // 0-25% emergence (2s @8s)
  transitionEnd: 0.30, // 25-30% transition
  readingEnd: 0.80, // 30-80% reading (4s @8s)
  convergenceEnd: 0.92, // 80-92% convergence (~1s @8s)
  revelationEnd: 1.0, // 92-100% revelation
} as const;

/**
 * Zoom steps for letterZoom variant emergence phase
 * Extended to form ~3 lines before transition
 * First step (letter) has short duration (0.08), rest are uniform (0.115 each)
 *
 * Scale values are applied as CSS transform: scale()
 * Base font size is 18px, so scale 8 = 144px effective
 * At scale 1.0 with ~35 words, text naturally wraps to 3 lines
 *
 * Total weights: 0.08 + (0.115 * 8) = 1.0
 */
export const LETTER_ZOOM_STEPS = [
  { scale: 8, getTextLength: 1, label: 'Letter', weight: 0.08 }, // Flash: ~0.5s at 144px
  { scale: 5, getTextLength: 'word', label: 'Word', weight: 0.115 }, // First word at 90px
  { scale: 3.5, getTextLength: 2, label: '2 Words', weight: 0.115 },
  { scale: 2.5, getTextLength: 4, label: '4 Words', weight: 0.115 },
  { scale: 1.8, getTextLength: 7, label: 'Line 1', weight: 0.115 }, // ~7 words = 1 line
  { scale: 1.4, getTextLength: 12, label: 'Line 1.5', weight: 0.115 }, // ~12 words
  { scale: 1.15, getTextLength: 18, label: 'Line 2', weight: 0.115 }, // ~18 words = 2 lines
  { scale: 1.05, getTextLength: 25, label: 'Line 2.5', weight: 0.115 }, // ~25 words
  { scale: 1.0, getTextLength: 'lines3', label: 'Line 3', weight: 0.115 }, // 3 full lines
] as const;

/** Type for zoom step text extraction */
export type ZoomStepTextLength = number | 'word' | 'lines3';

/** Zoom step definition with weighted timing */
export interface ZoomStep {
  scale: number;
  getTextLength: ZoomStepTextLength;
  label: string;
  weight: number;
}

/** Dynamic zoom step with numeric word count (for generated steps) */
export interface DynamicZoomStep {
  scale: number;
  wordCount: number;
  label: string;
  weight: number;
  /** If set, use pre-split readingLines instead of words for text extraction (for seamless transition) */
  lineCount?: number;
}

/**
 * Generates dynamic zoom steps for Letter Zoom emergence phase
 * Targets 40-50% content coverage with logarithmic step count.
 * Capped at MAX_EMERGENCE_LINES (5 lines = 35 words) to prevent overwhelming users.
 *
 * @param totalWords - Total word count in text
 * @param targetCoverage - Target coverage ratio (default 0.45 = 45%)
 * @returns Array of zoom steps with scale, wordCount, label, and weight
 *
 * @example
 * // 50 words → ~7 steps covering 23 words (~3 lines)
 * generateDynamicZoomSteps(50)
 *
 * // 200 words → capped at 35 words (~5 lines max)
 * generateDynamicZoomSteps(200)
 *
 * @see ANIMATION_LINE_CONSTANTS - Configuration for line cap and coverage
 */
export function generateDynamicZoomSteps(
  totalWords: number,
  targetCoverage: number = ANIMATION_LINE_CONSTANTS.TARGET_COVERAGE
): DynamicZoomStep[] {
  const { MAX_EMERGENCE_LINES, WORDS_PER_LINE } = ANIMATION_LINE_CONSTANTS;

  // Edge case: very short text
  if (totalWords < 5) {
    return [
      { scale: 8, wordCount: 1, label: 'Letter', weight: 0.2 },
      { scale: 4, wordCount: Math.max(2, totalWords), label: 'All', weight: 0.8 },
    ];
  }

  // Calculate target word count (40-50% coverage), capped to MAX_EMERGENCE_LINES
  const maxWordsForLines = MAX_EMERGENCE_LINES * WORDS_PER_LINE; // 5 * 7 = 35 words
  const targetWords = Math.min(
    Math.ceil(totalWords * targetCoverage),
    maxWordsForLines
  );

  // Logarithmic step count: 6-12 based on target words
  // Fewer steps for short text, more for long
  const numSteps = Math.min(Math.max(Math.ceil(Math.log2(targetWords)) + 2, 6), 12);

  // Exponential growth factor for word progression
  // Each step shows ~1.8x more words than previous
  const growthFactor = Math.pow(targetWords, 1 / (numSteps - 1));

  // First step weight (fast flash)
  const firstStepWeight = 0.08;
  // Remaining weight distributed equally
  const remainingWeight = 1 - firstStepWeight;
  const otherStepWeight = remainingWeight / (numSteps - 1);

  // Scale range: start high, end at 1.0
  const maxScale = 8;
  const minScale = 1;

  const steps: DynamicZoomStep[] = [];

  for (let i = 0; i < numSteps; i++) {
    // Word count: exponential growth (1, 2, 4, 8, ... targetWords)
    const wordCount = i === 0 ? 1 : Math.min(Math.ceil(Math.pow(growthFactor, i)), targetWords);

    // Scale: easeOutCubic from maxScale to minScale
    const t = i / (numSteps - 1);
    const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
    const scale = Number((maxScale - (maxScale - minScale) * eased).toFixed(2));

    // Weight: first step fast, rest equal
    const weight = i === 0 ? firstStepWeight : otherStepWeight;

    // Generate label based on word count
    let label: string;
    // Estimate line count, capped to MAX_EMERGENCE_LINES
    const estimatedLines = Math.min(
      Math.max(1, Math.ceil(wordCount / WORDS_PER_LINE)),
      MAX_EMERGENCE_LINES
    );

    if (i === 0) {
      label = 'Letter';
    } else if (wordCount === 1) {
      label = 'Word';
    } else if (wordCount <= 3) {
      label = `${wordCount} Words`;
    } else if (wordCount <= 10) {
      label = `~${wordCount} Words`;
    } else {
      label = estimatedLines <= 1 ? 'Line 1' : `~${estimatedLines} Lines`;
    }

    // For final steps (scale ≤ 2), use line-based targeting for seamless transition to reading
    // This ensures emergence END matches reading START exactly
    const useLineBasedTargeting = scale <= 2 && estimatedLines >= 1;

    steps.push({
      scale,
      wordCount,
      label,
      weight: Number(weight.toFixed(4)),
      // Add lineCount for steps where we want line-based rendering
      ...(useLineBasedTargeting && { lineCount: estimatedLines }),
    });
  }

  // Normalize weights to ensure they sum to 1.0
  const totalWeight = steps.reduce((sum, s) => sum + s.weight, 0);
  if (Math.abs(totalWeight - 1) > 0.001) {
    const lastStep = steps[steps.length - 1];
    lastStep.weight = Number((lastStep.weight + (1 - totalWeight)).toFixed(4));
  }

  return steps;
}

/**
 * Pre-calculates step boundaries from dynamic steps
 * Returns array of [start, end] tuples for O(1) step lookup
 *
 * @param steps - Array of zoom steps with weights
 * @returns Array of [start, end] tuples representing cumulative weight boundaries
 */
export function calculateDynamicStepBoundaries(
  steps: DynamicZoomStep[]
): Array<[number, number]> {
  const boundaries: Array<[number, number]> = [];
  let cumulative = 0;

  for (const step of steps) {
    const start = cumulative;
    cumulative += step.weight;
    boundaries.push([start, Math.min(cumulative, 1)]);
  }

  return boundaries;
}

/** Y2K gradient colors for progress bar */
export const Y2K_GRADIENT_COLORS = {
  pink: '#FFB3E6',
  lavender: '#D4A5FF',
  mint: '#A5FFEB',
} as const;

/** Focus blur levels for reading phase text lines */
export const FOCUS_BLUR_LEVELS = {
  0: { blur: 0, opacity: 1, scale: 1.02 },       // Focused line
  1: { blur: 0.8, opacity: 0.7, scale: 1 },
  2: { blur: 1.8, opacity: 0.45, scale: 1 },
  3: { blur: 3, opacity: 0.25, scale: 1 },
  4: { blur: 4.5, opacity: 0.12, scale: 1 },     // Most blurred
} as const;

/**
 * Unified Animation Line Constants
 *
 * Controls line limits across emergence and reading phases for balanced pacing.
 *
 * Pacing Analysis @8s (Express 3x speed):
 * - Emergence (25% = 2.0s): 3 lines → ~667ms/line
 * - Reading (50% = 4.0s): 8 lines → ~500ms/line
 *
 * Reading slightly faster than emergence for natural scroll feel.
 *
 * @see generateDynamicZoomSteps - Uses MAX_EMERGENCE_LINES
 * @see calculateReadingWindow - Uses MAX_READING_LINES
 * @see LetterZoomAnimation.web.tsx - reading phase scroll logic
 * @see LetterZoomAnimation.native.tsx - reading phase scroll logic
 */
export const ANIMATION_LINE_CONSTANTS = {
  /** Maximum lines to show at end of emergence phase (reduced for balanced pacing) */
  MAX_EMERGENCE_LINES: 3,
  /** Maximum lines to scroll through in reading phase */
  MAX_READING_LINES: 8,
  /** Assumed words per line for line estimation */
  WORDS_PER_LINE: 7,
  /** Starting position in text (0.35 = 35% from start, avoids repetition with emergence) */
  WINDOW_START_RATIO: 0.35,
  /** Target content coverage ratio for emergence (45% of total words) */
  TARGET_COVERAGE: 0.45,
} as const;

/**
 * Convergence Phase Constants
 *
 * Visual transformation during convergence (80-92%):
 * - Text shrinks from 1.0 → 0.75 scale (shows more content)
 * - Centers on middle line
 * - Blur reduces from current → 0px (text clears)
 * - Opacity increases from 0.5 → 1.0 (fully visible)
 *
 * Easing: Implementations use easeOutQuad for smooth deceleration
 *   formula: 1 - Math.pow(1 - progress, 2)
 *
 * Platform-specific behavior:
 * - Web: Full blur + opacity animation (CSS filter: blur)
 * - Native: Opacity only (React Native doesn't support CSS filter: blur)
 *
 * @see LetterZoomAnimation.web.tsx - convergence phase logic (lines 263-297)
 * @see LetterZoomAnimation.native.tsx - convergence phase logic (lines 327-354)
 * @see Reference: .superdesign/design_iterations/book_scanner_6_letter_zoom.html lines 929-949
 */
export const CONVERGENCE_CONSTANTS = {
  /** Final scale after convergence (75% of original) */
  END_SCALE: 0.75,
  /** Scale reduction factor (1 - END_SCALE) */
  SCALE_REDUCTION: 0.25,
  /**
   * Starting opacity for convergence phase - MUST be 1.0 for "white and clear" text
   *
   * Multi-agent + multi-model consensus (2025-12-13):
   * - Gemini Pro 3 (10/10): "Zero-risk, maximizes dramatic contrast for revelation"
   * - GPT O3 (8/10): "Improves WCAG accessibility, current 0.5 violates AA contrast"
   * - @debugger: Root cause of dim text during shrink phase
   * - @code-reviewer: CRITICAL fix - text starts dimmed instead of clear
   * - @cto: APPROVED - aligns with "Reality TV narrator" visual metaphor
   *
   * User requirement: Text must be WHITE and CLEAR during convergence/shrink,
   * dim effect applies ONLY after badge reveals in revelation phase.
   */
  START_OPACITY: 1.0,
  /** Ending opacity (full visibility) */
  END_OPACITY: 1.0,
  /** Maximum blur in pixels that will reduce to 0 (web only, native uses opacity for clarity) */
  MAX_BLUR: 2.5,
} as const;

/**
 * Revelation Phase Constants
 *
 * Visual transformation during revelation (93-100%):
 * - Text container dims from 1.0 → 0.12 opacity (becomes backdrop)
 * - Badge appears with spring animation after delay
 * - Glass effects hidden
 *
 * Reference: .superdesign/design_iterations/book_scanner_6_letter_zoom.html lines 950-964
 *
 * @see LetterZoomAnimation.web.tsx - revelation phase logic
 * @see LetterZoomAnimation.native.tsx - revelation phase logic
 */
export const REVELATION_CONSTANTS = {
  /** Text container opacity when badge appears (12% - visible but de-emphasized) */
  TEXT_BACKDROP_OPACITY: 0.50,
  /** Delay before badge animation starts (ms) - allows text to dim first */
  BADGE_DELAY_MS: 80,
  /** Duration for text dimming animation (ms) */
  TEXT_DIM_DURATION_MS: 150,
} as const;

/**
 * Calculates reading window bounds for optimized scrolling
 *
 * For texts <= MAX_READING_LINES: scroll through all lines (no window)
 * For longer texts: create 8-line window starting at 35% position
 *
 * @param actualLineCount - Total lines excluding padding
 * @returns [windowStart, windowEnd] - Line indices for window bounds (inclusive)
 *
 * @example
 * // Short text (8 lines or less) - no windowing
 * calculateReadingWindow(8) // => [0, 7]
 *
 * // Long text (30 lines) - 8-line window at 35%
 * calculateReadingWindow(30) // => [10, 17]
 *
 * // Very long text (50 lines) - 8-line window at 35%
 * calculateReadingWindow(50) // => [17, 24]
 */
export function calculateReadingWindow(actualLineCount: number): [number, number] {
  const { MAX_READING_LINES, WINDOW_START_RATIO } = ANIMATION_LINE_CONSTANTS;

  // Guard against invalid input
  if (actualLineCount <= 0) {
    return [0, 0];
  }

  // Short texts: use all lines (no windowing)
  if (actualLineCount <= MAX_READING_LINES) {
    return [0, actualLineCount - 1];
  }

  // Long texts: 8-line window starting at 35% position
  const windowStart = Math.floor(actualLineCount * WINDOW_START_RATIO);

  // Ensure window doesn't exceed text bounds
  const maxStartPosition = actualLineCount - MAX_READING_LINES;
  const clampedStart = Math.min(windowStart, maxStartPosition);

  const windowEnd = clampedStart + MAX_READING_LINES - 1;

  return [clampedStart, windowEnd];
}

// ============================================================================
// THEATRICAL TIME ARCHITECTURE
// ============================================================================

/**
 * Theatrical Time Architecture
 *
 * The Letter Zoom animation serves dual purposes:
 * 1. **Entertainment**: Engaging visual experience during AI processing
 * 2. **Strategic Buffer**: Guaranteed minimum duration for backend operations
 *
 * This is NOT wasted time - it's "theatrical time" that:
 * - Builds anticipation for the drama score reveal
 * - Creates perceived value ("AI is analyzing deeply")
 * - Provides extensible slots for future content injection
 *
 * ## Visual Metaphor: "AI Processing"
 *
 * The 5-phase animation represents AI "reading and analyzing" the text:
 * 1. **Emergence** (55%): AI "receiving" the message (zoom from letter to lines)
 * 2. **Transition** (5%): AI "internalizing" the content
 * 3. **Reading** (25%): AI "scanning" the full text (saccadic scroll)
 * 4. **Convergence** (8%): AI "synthesizing" insights (text shrinks)
 * 5. **Revelation** (7%): AI "delivering" the verdict (badge spring)
 *
 * Users don't see "waiting for API" - they see "AI dramatically analyzing your message"
 *
 * ## Future Content Injection Points
 *
 * The theatrical time creates natural slots for premium content:
 *
 * | Phase | Content Opportunity | Tier |
 * |-------|---------------------|------|
 * | Emergence | Narrator Quips ("Oh, this looks interesting...") | L2+ |
 * | Emergence | Pattern Teasers (flash icons for detected patterns) | L3+ |
 * | Reading | Pattern Spotlight (highlight phrases with labels) | L2+ |
 * | Reading | Character Reactions (narrator expressions) | L3+ |
 * | Convergence | Insight Countdown ("3 patterns detected...") | L2+ |
 * | Convergence | Severity Indicator (color shift based on score) | L2+ |
 * | Revelation | Transformation Insights ("2 manipulation tactics") | L3+ |
 * | Revelation | Practitioner Insights ("triangulation pattern") | L4 |
 *
 * ## Psychological Framing
 *
 * Research shows users perceive:
 * - **Instant results** as "simple/automated" (less valuable)
 * - **Brief wait with animation** as "processing/analyzing" (more valuable)
 * - **5-7 seconds** as optimal "analysis time" for perceived thoroughness
 *
 * DramaDetector's 8s Express animation hits this sweet spot.
 *
 * @see LETTER_ZOOM_PHASE_TIMING - Phase percentage allocations
 * @see ANIMATION_LINE_CONSTANTS - Long text optimization
 * @see packages/drama-detector-docs/features/animation/THEATRICAL_TIME_ARCHITECTURE.md
 */

/**
 * Animation Duration Constants
 *
 * Controls the minimum and base duration for Letter Zoom animation.
 *
 * ## Balanced Pacing Design
 *
 * Duration formula: `baseDuration / speedMultiplier`
 * - With BASE_DURATION = 24000ms:
 *   - Express (3x) = 8.0s ✅ (balanced pacing)
 *   - Efficient (2x) = 12.0s ✅ (relaxed experience)
 *   - Contemplative (1x) = 24.0s ✅ (intentionally meditative)
 *
 * ## Phase Pacing Balance
 *
 * | Phase | Lines | Time @8s | Per-Line |
 * |-------|-------|----------|----------|
 * | Emergence | 3 | 2.0s | ~667ms |
 * | Reading | 6 | 4.0s | ~667ms |
 *
 * Balanced 1:1 pacing ratio ensures consistent visual rhythm.
 *
 * ## Phase Breakdown at Express (8s)
 *
 * | Phase | % | Duration | Per-Unit Pace |
 * |-------|---|----------|---------------|
 * | Emergence | 25% | 2.0s | ~3 zoom steps |
 * | Transition | 5% | 0.4s | crossfade |
 * | Reading | 50% | 4.0s | ~667ms/line (6 lines) |
 * | Convergence | 12% | 0.96s | shrink animation |
 * | Revelation | 8% | 0.64s | badge spring |
 *
 * Multi-Model Consensus: Gemini Pro 3 + GPT O3 validated this approach.
 *
 * @see calculateAnimationDuration - Uses these constants
 * @see LetterZoomAnimation.web.tsx - Web implementation
 * @see LetterZoomAnimation.native.tsx - Native implementation
 */
export const ANIMATION_DURATION_CONSTANTS = {
  /**
   * Base duration in milliseconds (24 seconds)
   *
   * Divided by speed multiplier to get final duration.
   * - Contemplative (1x): 24000 / 1.0 = 24.0s
   * - Efficient (2x): 24000 / 2.0 = 12.0s
   * - Express (3x): 24000 / 3.0 = 8.0s
   *
   * Increased from 15s to 24s for balanced pacing:
   * - Emergence: 2s dramatic reveal (25%)
   * - Reading: 4s relaxed scrolling (50%)
   * - Convergence: ~1s shrink (12%)
   */
  BASE_DURATION: 24000,

  /**
   * Minimum total duration floor in milliseconds (10 seconds)
   *
   * Guarantees sufficient time for:
   * 1. LLM response (~4s average) + variance buffer
   * 2. Readable reading phase (~667ms/line)
   * 3. Balanced pacing across all phases
   *
   * Applied as: `Math.max(calculated, MIN_TOTAL_DURATION)`
   */
  MIN_TOTAL_DURATION: 10000,
} as const;

/**
 * Calculates animation duration with minimum floor guarantee
 *
 * Ensures animation duration respects both:
 * 1. Speed preset multiplier preference
 * 2. Minimum duration floor (for balanced pacing)
 *
 * @param speedMultiplier - Speed preset multiplier (1.0 to 3.0)
 * @returns Duration in milliseconds (minimum 10000ms)
 *
 * @example
 * // Express speed (3x) - clamped to minimum
 * calculateAnimationDuration(3.0) // => 10000 (not 8000)
 *
 * // Contemplative speed (1x) - uses calculated value
 * calculateAnimationDuration(1.0) // => 24000
 *
 * // Efficient speed (2x) - uses calculated value
 * calculateAnimationDuration(2.0) // => 12000
 */
export function calculateAnimationDuration(speedMultiplier: number): number {
  const { BASE_DURATION, MIN_TOTAL_DURATION } = ANIMATION_DURATION_CONSTANTS;

  // Calculate duration from speed multiplier
  const calculated = BASE_DURATION / speedMultiplier;

  // Apply minimum floor to ensure LLM latency buffer
  return Math.max(calculated, MIN_TOTAL_DURATION);
}

/**
 * Tier-Based Content Density for Theatrical Time
 *
 * Defines how much additional content each tier receives
 * during animation phases. Higher tiers see more insights.
 *
 * Content examples by tier:
 * - L1 (Free): Base animation only
 * - L2 (Drama+): Narrator quips, 1 pattern highlight, category insight
 * - L3 (DramaX): Pattern icons, full labels, transformation insights
 * - L4 (Practitioner): Clinical framing, evidence markers, risk assessment
 *
 * @future Phase 2 implementation - content injection system
 */
export const THEATRICAL_CONTENT_DENSITY = {
  /** Free tier - base animation, no additional content */
  free: 0,
  /** Drama+ - light enhancement (narrator quips, single highlights) */
  drama_plus: 0.3,
  /** DramaX - moderate enhancement (pattern labels, insights) */
  drama_x: 0.6,
  /** Practitioner - full enhancement (clinical framing, evidence) */
  practitioner: 1.0,
} as const;

/**
 * Narrator Subtitle Constants
 *
 * Configuration for "producer commentary" subtitles during Theatrical Time.
 * Subtitles appear ONLY during safe beats (Emergence, Convergence) - NEVER during Reading
 * to avoid "dual-task interference" (cognitive overload from competing text).
 *
 * Multi-Model Consensus (8/10 confidence):
 * - Gemini 2.5 Pro: "Masterstroke for brand reinforcement"
 * - GPT-5 Pro: "Right feature, wrong timing during Reading phase"
 * - Both agree: Use Emergence (0-2s) and Convergence (6.4-7.4s) only
 *
 * @see packages/docs-site/src/content/docs/product/features/theatrical-time/content-injection.md
 */
export const NARRATOR_SUBTITLE_CONSTANTS = {
  /** Progress within Emergence phase when subtitle STARTS showing (25% = 0.5s @ 2s emergence) */
  EMERGENCE_SHOW_START: 0.25,
  /** Progress within Emergence phase when subtitle ENDS showing (90% = 1.8s @ 2s emergence) */
  EMERGENCE_SHOW_END: 0.90,
  /** Maximum quip length in characters for fallback quips (fits single line, quick read) */
  MAX_QUIP_LENGTH: 60,
  /** Maximum length for LLM narrator intro (2-3 sentences, ~150-200 chars typical) */
  MAX_LLM_NARRATOR_LENGTH: 200,
  /** Subtitle display time in milliseconds (matches reality TV pacing) */
  DISPLAY_DURATION_MS: 1400,
  /** Fade in/out animation duration in milliseconds */
  FADE_DURATION_MS: 200,
  /** Number of quips per session (1 = avoid fatigue) */
  QUIPS_PER_SESSION: 1,
} as const;
